import React, { useEffect, useState } from "react";
import moment from "moment";
import TaskService from "../../services/task.service";
import DynamicTable from "../reuse_components/DynamicTable";
import CustomPagination from "../subComponents/pagination/CustomPagination";
import ItemsPerPageSelector from "../subComponents/pagination/ItemsPerPageSelector";
import SearchInput from "../subComponents/search/SearchInput";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import GenericFilter, { FilterBar } from "../subComponents/Filter/GenericFilters";
import CustomFilterSelect from "../designSystem/CustomFilterSelect";
import { t } from "../../locales/translate";
import { PERMISSIONS } from "../../Permissions";

// Standalone module: lets a logged-in user see the last access of everyone
// reporting to them. Reuses the existing (permission-guarded) SubOrdinates
// endpoint with server-side pagination and keeps its own local state so it
// never touches shared Redux data.
const TeamAccess = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const permissions = JSON.parse(localStorage.getItem("permissionsList")) || [];
  const employeeId =
    user?.result?.[0]?.user?.id !== undefined ? user.result[0].user.id : null;

  const canViewSubOrdinate = permissions.includes(
    PERMISSIONS.CAN_VIEW_SUBORDINATE
  );

  const firstPage = 1;
  const [subordinates, setSubordinates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [todayAccessedFilter, setTodayAccessedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // '' => all, 'true' => accessed today, 'false' => not accessed today.
  const accessedToday =
    todayAccessedFilter === "" ? undefined : todayAccessedFilter === "true";

  // Fetch one page whenever page / page size / search / filter changes.
  useEffect(() => {
    if (!employeeId || !canViewSubOrdinate) return;
    let isMounted = true;
    setIsLoading(true);
    TaskService.getSubOrdinates(
      employeeId,
      currentPage,
      itemsPerPage,
      searchQuery,
      accessedToday
    )
      .then((res) => {
        if (!isMounted) return;
        const result = res?.data?.result;
        setSubordinates(Array.isArray(result) ? result : []);
        setTotalItems(res?.data?.totalCount || 0);
        setTotalPages(res?.data?.totalPages || 0);
      })
      .catch(() => {
        if (!isMounted) return;
        setSubordinates([]);
        setTotalItems(0);
        setTotalPages(0);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [employeeId, canViewSubOrdinate, currentPage, itemsPerPage, searchQuery, accessedToday]);

  const rows = subordinates.map((emp) => ({
    name: `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "-",
    designationName: emp.designationName || "-",
    reportToName: emp.reportToName || "-",
    lastAccess: emp.lastAccessed
      ? moment(emp.lastAccessed).format("MMM DD, YYYY hh:mm A")
      : t("never"),
  }));

  const columns = [
    { label: t("employee"), accessor: "name" },
    { label: t("designation"), accessor: "designationName" },
    { label: t("reportsTo"), accessor: "reportToName" },
    { label: t("lastAccess"), accessor: "lastAccess" },
  ];

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(firstPage);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value) || 10);
    setCurrentPage(firstPage);
  };

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const handleTodayAccessedFilterChange = (event) => {
    setTodayAccessedFilter(event.target.value);
    setCurrentPage(firstPage);
  };

  const handleClearAllFilters = () => {
    setTodayAccessedFilter("");
    setCurrentPage(firstPage);
  };

  if (!canViewSubOrdinate) {
    return (
      <div style={{ padding: "24px" }}>
        <p>{t("noDataFound")}</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <LoadingOverlay isVisible={isLoading} />
        <div className="main-container">
          <div className="content-wrapper p-0">
            {/* Header with Search, Pagination, and Filter Toggle */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 16px",
              }}
            >
              <div>
                <SearchInput
                  placeholder={t("search")}
                  onChange={handleSearch}
                  value={searchQuery}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ minWidth: "80px" }}>
                  <ItemsPerPageSelector
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  />
                </div>

                <CustomPagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  totalPages={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
                />

                <GenericFilter
                  showFilters={showFilters}
                  toggleFilters={toggleFilters}
                />
              </div>
            </div>

            {/* Filter Bar using Generic Filter Component */}
            <FilterBar showFilters={showFilters} onClearAll={handleClearAllFilters}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  gap: "12px",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div style={{ position: "relative", minWidth: "150px", flexShrink: 0 }}>
                  <CustomFilterSelect
                    label={t("accessedToday")}
                    width="150px"
                    value={todayAccessedFilter}
                    searchable={false}
                    onChange={handleTodayAccessedFilterChange}
                    options={[
                      { id: "", name: "All" },
                      { id: "true", name: t("accessedToday") },
                    ]}
                  />
                </div>
              </div>
            </FilterBar>
          </div>
          <DynamicTable
            data={rows}
            columns={columns}
            emptyMessage={t("noDataFound")}
            showPagination={totalItems > 0}
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>
    </>
  );
};

export default TeamAccess;
